// Global code
if (!String.prototype.replaceAll) {
    String.prototype.replaceAll = function (str, newStr) {

        // If a regex pattern
        if (Object.prototype.toString.call(str).toLowerCase() === '[object regexp]') {
            return this.replace(str, newStr);
        }

        // If a string
        return this.replace(new RegExp(str, 'g'), newStr);

    };
}

if ( !__DEV__ ) {
    console.log = () => {};
}

// case-insensitive substring predicate
const ciin = (x, y) => y.toLowerCase().indexOf(x.toLowerCase()) > -1;

const isChrome = ciin('chrome', navigator.userAgent);
const isSamsungBrowser = ciin('SamsungBrowser', navigator.userAgent);
const isXiaomiBrowser = ciin('MiuiBrowser', navigator.userAgent);
const isAndroid = ciin('Android', navigator.userAgent);


const redirectCondition = isSamsungBrowser || isXiaomiBrowser || (!isChrome && isAndroid);

if (redirectCondition) {
    let deeplink = `googlechrome://navigate?url=${window.location.toString()}`;
    let intent = `intent://navigate?url=${window.location.toString()}#Intent;scheme=googlechrome;package=com.android.chrome;end`;

    // try several methods until one ends up working
    window.location = intent;
    window.location = deeplink.toString();
    window.location.assign(deeplink.toString());
    window.open(deeplink.toString());
}

// React && Own-lib
import { useState, useCallback, useEffect } from 'react';
import { ActivityIndicator, Linking, SafeAreaView, Image } from 'react-native';
import { MenuScreen } from '@scenes';
import { Typography, Spacing } from '@styles';

// Providers
import { AppearanceProvider } from 'react-native-appearance';
import ThemeProvider, { useTheme } from '@themeProvider';
import { LanguageProvider, useLanguage } from '@languageProvider';

// Native libs
import * as Font from 'expo-font';
import * as Updates from 'expo-updates';
import * as SplashScreen from 'expo-splash-screen';
import Constants from 'expo-constants';
import * as Sentry from 'sentry-expo';

import { ModalProvider, SnackbarProvider, useModal } from '@lib';
import service, { Events, Places, Profile, Promotions } from '@services';
import { TRACKING_CONFIG } from '@services/trackings';
import { ProductNode, ProductPack, Promotion } from '@datamodel';

Sentry.init({
    dsn: 'https://85bc3c99eef04d5aaaa2b5c570cf36e4@glitchtip.corp.negativeepsilon.com/16',
    enableInExpoDevelopment: true,
    debug: __DEV__,
    environment: Constants.manifest.extra.SENTRY_ENVIRONMENT
});

const StatefulApp = () => {
    const [appReady, setAppReady] = useState(false);
    const [state, setState] = useState({
        placeId: '', eventId: '', place: {},
        menu: {}, packs: {}, pickupBars: {}, promotions: {},
        isAuth: false
    });
    const { translate } = useLanguage();
    const { pushAlert } = useModal();
    const { useColor, setColors } = useTheme();
    const [backgroundColor, primaryColor] = useColor(['background', 'primary'])

    const refreshMenu = useCallback(async () => {
        try {
            const response = await Events.getMenu(state.eventId);
            setState({
                ...state,
                menu: response.menu
            })
        } catch (error) {
            console.log(error);
        }
    }, [state.eventId]);

    const addPromotions = (promotions) => setState(prev => ({
        ...prev,
        promotions: {
            ...prev.promotions,
            ...promotions
        }
    }));

    useEffect(() => {
        const prepare = async () => {
            try {
                await SplashScreen.preventAutoHideAsync();

                // ASYNC ON LOAD STUFF
                try {
                    await Font.loadAsync({ ...Typography.FONTS });
                } catch (e) {
                    console.warn('Error while loading fonts:', e);
                }

                let eventId, placeId, profile;
                const url = await Linking.getInitialURL();
                const urlObj = new URL(url);
                const searchParams = new URLSearchParams(urlObj.search);

                // OAuth flow
                // Login check
                service.api_token = window.localStorage.getItem('oauthAccessToken');
                try {
                    // Get user related data
                    if (service.api_token) {
                        profile = (await Profile.get()).profile;
                    }
                } catch (e) {
                    console.warn('Not logged in: Get profile', e);
                    service.api_token = null;
                    window.localStorage.removeItem('oauthAccessToken');
                }

                // Ongoing OAuth request check
                try {
                    if (!service.api_token && searchParams.get('code') && window.localStorage.getItem('oauthReqData')) {
                        const code = searchParams.get('code');
                        await service.exchangeOAuthCode(code);

                        // Get user related data
                        profile = (await Profile.get()).profile;
                    }
                } catch (e) {
                    console.warn('Not logged in: OAuth request', e);
                    service.api_token = null;
                }
                
                if (searchParams.has('event') && searchParams.has('place')) {
                    eventId = searchParams.get('event');
                    placeId = searchParams.get('place');
                } else if ( searchParams.get('state') ) {
                    // Encoded event, place in OAuth flow
                    const oauthState = JSON.parse(atob(searchParams.get('state')));
                    eventId = oauthState.event;
                    placeId = oauthState.place;
                }

                if (!eventId || !placeId) {
                    throw "event_not_found";
                }

                // Try login when coming back from a purchase and it logged the user in
                try {
                    if (!service.api_token && searchParams.get('try_login')) {
                        await service.createOAuth(btoa(JSON.stringify({
                            event: eventId,
                            place: placeId
                        })));
                    }
                } catch (e) {
                    console.warn('Not logged in: Try login', e)
                }

                // Get menu
                let menuInfo;
                try {
                    menuInfo = await Events.getMenu(eventId)
                } catch (e) {
                    console.error('No menu found', e);
                }

                // Get place
                const { place: placeInfo, pickupBars, business, availableEvents } = await Places.get(placeId);

                if (availableEvents.indexOf(eventId) < 0 || !menuInfo) {
                    window.location = service._build_url(`tripay/${placeInfo.slug}/qr/`);
                }

                // Get and format packs
                const { packRoots, menu } = menuInfo;
                const { packs } = await Events.getPacks(eventId);
                ProductNode.propagatePrices(menu);
                const fullMenu = ProductNode._addChildren(menu, packRoots);
                const fullPacks = ProductPack.collectionAddInformation(packs, fullMenu);

                // Get promotions
                const { promotions } = await Promotions.list({
                    festea: true,
                    place: placeId
                });

                setState({
                    place: placeInfo,
                    placeId, eventId,
                    promotions: Promotion.collectionFilterByPlaceId(promotions, placeId),
                    menu: fullMenu,
                    packs: fullPacks, ...placeInfo, pickupBars, business,
                    isAuth: !!profile, // TODO: Change this later
                    profile
                })

                if (placeInfo.primaryColor) {
                    setColors({
                        primary: placeInfo.primaryColor,
                        secondary: placeInfo.secondaryColor
                    });

                    // Set meta color in Chrome
                    const themeMeta = document.createElement('meta');
                    themeMeta.setAttribute('name', 'theme-color');
                    themeMeta.setAttribute('content', placeInfo.primaryColor);
                    document.head.append(themeMeta);
                }

                // Set tracking config
                TRACKING_CONFIG.path = urlObj.pathname;
                TRACKING_CONFIG.hostname = urlObj.hostname;
                TRACKING_CONFIG.slug = placeInfo.slug;
                TRACKING_CONFIG.place = placeId;
                TRACKING_CONFIG.event = eventId;

                setAppReady(true);
            } catch (e) {
                if (e === "event_not_found") {
                    pushAlert({ title: 'Error', text: translate('eventNotFound'), actions: [] })
                } else {
                    pushAlert({ title: 'Error', text: translate('somethingWentWrong'), actions: [] });
                }
                console.warn(e);
            }
        }

        prepare();
        
        // const showAlcoholNotice = window.localStorage.getItem('alcoholNotice') !== 'doNotShow';
        // if (showAlcoholNotice) {
        //     pushAlert({ title: translate('notice'), text: translate('bannedAlcohol'), actions: [{
        //         function: () => {
        //             window.localStorage.setItem('alcoholNotice', 'doNotShow');
        //             return true;
        //         },
        //         displayText: translate("dontShowAgain"),
        //         type: 'main'
        //     }]});
        // }
    }, []);

    const onLayoutRootView = useCallback(async () => {
        if (appReady) {
            await SplashScreen.hideAsync();
        }
    }, [appReady]);


    if (!appReady) {
        return <SafeAreaView style={{ flexGrow: 1, backgroundColor, alignItems: 'center', justifyContent: 'center' }}>
            <Image source={require('@assets/images/FesteaPOS.webp')} style={{ width: 150, height: 150, marginBottom: Spacing.M }} />
            <ActivityIndicator size="large" color={primaryColor}/>
        </SafeAreaView>;
    }

    return (<MenuScreen {...state} refreshMenu={refreshMenu} addPromotions={addPromotions} onLayout={onLayoutRootView} />)
}

export default function App() {

    return (
        <LanguageProvider>
            <AppearanceProvider>
                <ThemeProvider>
                    <SnackbarProvider>
                        <ModalProvider>
                            <StatefulApp />
                        </ModalProvider>
                    </SnackbarProvider>
                </ThemeProvider>
            </AppearanceProvider>
        </LanguageProvider>
    );
}
